import { frappeList, frappeGet } from "./utils";
import { getERPClient } from "./client";
import type { Item, ItemWithVariants, ItemPrice, ItemStock } from "./types/item";
import type { FrappeListResponse } from "@/lib/frappe-client";
import type { FrappeFilterArray } from "./filterMapper";
import { 
  docTypeListWithPagination, 
  docTypeListWithPaginationViaMethod 
} from "./docs";

/**
 * Default fields to fetch for Item list views
 * 
 * This constant ensures consistency across:
 * - items.list() (legacy)
 * - items.listWithPagination() defaultFields
 * - useInventoryListWithPagination fields
 * - createItemColumns accessorKeys
 * 
 * IMPORTANT:
 * - If you add a new column to createItemColumns, add the corresponding field here.
 * - Note: "modified" is used for default sorting but not included here (Frappe can sort on fields
 *   not in the fields array). If you add a "Last Updated" column later, add "modified" to this array.
 */
export const ITEM_LIST_FIELDS = [
  "name",
  "item_code",
  "item_name",
  "stock_uom",
  "image",
  "is_stock_item",
  "item_group",
  "standard_rate",
  "has_variants",
  "variant_of",  // this stays - real DB column
  // "is_variant" REMOVED – now computed in backend (not a DB column)
  "storefront_enabled",
  "storefront_slug",  // needed to build canonical storefront preview URLs (falls back to name)
  "disabled",
] as const;

/**
 * Field configuration for Item list settings
 * Only includes fields that are rendered as visible columns (excludes "name" and "image" which are not visible columns)
 */
export const ITEM_LIST_FIELD_CONFIG = [
  { fieldname: "item_code", label: "Item Code", required: true },
  { fieldname: "item_name", label: "Item Name", required: true },
  { fieldname: "item_group", label: "Item Group" },
  { fieldname: "stock_uom", label: "UOM" },
  { fieldname: "standard_rate", label: "Standard Rate" },
  { fieldname: "is_stock_item", label: "Stock Item" },
] as const;

/**
 * Default visible field order for Item list settings
 * Excludes "name" since it's not a visible column (only used for backend/row id)
 */
export const ITEM_LIST_VISIBLE_DEFAULT_FIELDS = [
  "item_code",
  "item_name",
  "item_group",
  "stock_uom",
  "standard_rate",
  "is_stock_item",
] as const;

export interface ItemListParams {
  filters?: FrappeFilterArray | Record<string, any>; // Support both array and object formats
  or_filters?: Array<[string, string, any]>; // Canonical format: [field, operator, value] (no doctype prefix)
  searchQuery?: string; // Combined search query (will be converted to or_filters in hook)
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  fields?: string[];
}

export interface ItemListWithVariantsParams {
  filters?: FrappeFilterArray;
  or_filters?: Array<[string, string, any]>;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  fields?: string[];
}

export const items = {
  /**
   * List all items (legacy method - returns array only)
   * @deprecated Use listWithPagination for server-side pagination
   */
  list(filters?: Record<string, any>): Promise<Item[]> {
    return frappeList<Item>("Item", filters, [...ITEM_LIST_FIELDS]);
  },

  /**
   * List items with server-side pagination and filtering
   * 
   * IMPORTANT:
   * - If _assigned_to filter is present, uses custom method zaviago_backend.api.item_list.list_items_with_assignment
   *   This bypasses Frappe's filter validation which rejects _assigned_to_filter as an invalid field
   * - Otherwise, uses standard /api/resource/Item endpoint
   * 
   * This method delegates to generic docTypeListWithPagination functions to avoid duplication.
   */
  listWithPagination(params: ItemListParams): Promise<FrappeListResponse<Item>> {
    const { filters = [], or_filters, pageIndex = 0, pageSize = 20, orderBy, fields } = params;
    
    // Default fields for Item
    const defaultFields = fields || [...ITEM_LIST_FIELDS];

    // Check if _assigned_to filter is present
    let hasAssignedToFilter = false;
    let assignedToFilter: any = undefined;
    const filterArray: FrappeFilterArray = [];

    // Always enforce disabled = 0 filter
    // NOTE: If caller also passes a disabled filter, they'll end up with two disabled filters.
    // If the caller's disabled value ≠ 0, they'll get no results (by design - UI should only show enabled items).
    filterArray.push(["Item", "disabled", "=", 0]);

    // Process filters and extract _assigned_to
    if (Array.isArray(filters)) {
      // Array format: [["Item", "field", "=", "value"], ...]
      for (const filter of filters) {
        if (Array.isArray(filter) && filter.length >= 3) {
          // Check if this is an _assigned_to filter
          const field = filter.length >= 4 ? filter[1] : filter[0];
          
          if (field === "_assigned_to") {
            hasAssignedToFilter = true;
            // Extract assignment filter - handle both formats
            const operator = filter.length >= 4 ? filter[2] : filter[1];
            const value = filter.length >= 4 ? filter[3] : filter[2];
      
            if (operator === "=" || operator === "equals") {
              // Single user or accumulate for ALL mode
              if (assignedToFilter === undefined) {
                assignedToFilter = [];
              }
              if (Array.isArray(assignedToFilter)) {
                assignedToFilter.push(value);
              }
            } else if (operator === "in" && Array.isArray(value)) {
              // ANY mode with multiple users
              assignedToFilter = value;
            }
          } else {
            // Regular filter - keep it (type assertion needed due to tuple constraints)
            filterArray.push(filter as FrappeFilterArray[number]);
          }
        } else {
          // Non-array filter - keep it
          filterArray.push(filter);
        }
      }
    } else {
      // Object format - extract _assigned_to and convert rest to array
      const { _assigned_to, ...restFilters } = filters as Record<string, any>;
      
      if (_assigned_to !== undefined) {
        hasAssignedToFilter = true;
        assignedToFilter = _assigned_to;
      }
      
      // Convert remaining filters to array format
      for (const [field, value] of Object.entries(restFilters)) {
        if (Array.isArray(value) && value.length === 2) {
          filterArray.push(["Item", field, value[0], value[1]]);
        } else {
          filterArray.push(["Item", field, "=", value]);
        }
      }
    }

    // If _assigned_to filter is present, use custom method that bypasses validation
    if (hasAssignedToFilter && assignedToFilter !== undefined) {
      // Encode _assigned_to_filter in filters for custom method
      filterArray.push(["Item", "_assigned_to_filter", "=", assignedToFilter]);
      
      // Delegate to generic method override function
      return docTypeListWithPaginationViaMethod<Item>({
        doctype: "Item",
        filters: filterArray,
        or_filters,
        pageIndex,
        pageSize,
        orderBy,
        method: "zaviago_backend.api.item_list.list_items_with_assignment",
        fields: defaultFields,
      });
    }

    // No _assigned_to filter - delegate to generic standard list function
    return docTypeListWithPagination<Item>({
      doctype: "Item",
      filters: filterArray,
      or_filters,
      pageIndex,
      pageSize,
      orderBy,
      fields: defaultFields,
    });
  },

  /**
   * Get a single item by name/code
   */
  detail(itemCode: string): Promise<Item> {
    return frappeGet<Item>("Item", itemCode);
  },

  /**
   * Get prices for an item
   */
  prices(itemCode: string): Promise<ItemPrice[]> {
    return frappeList<ItemPrice>("Item Price", { item_code: itemCode });
  },

  /**
   * Get stock levels for an item across all warehouses
   */
  stock(itemCode: string): Promise<ItemStock[]> {
    return frappeList<ItemStock>("Bin", { item_code: itemCode }, [
      "name",
      "warehouse",
      "actual_qty",
      "reserved_qty",
      "projected_qty",
      "ordered_qty",
      "item_code",
    ]);
  },

  /**
   * Create a new item
   */
  create(data: Record<string, any>): Promise<Item> {
    const client = getERPClient();
    return client.create({ doctype: "Item", data });
  },

  /**
   * Update an existing item
   */
  update(name: string, data: Record<string, any>): Promise<Item> {
    const client = getERPClient();
    return client.update({ doctype: "Item", name, data });
  },

  /**
   * Delete an item
   */
  delete(name: string): Promise<void> {
    const client = getERPClient();
    return client.delete({ doctype: "Item", name });
  },

  /**
   * List items with variants nested under their parent items
   * 
   * Uses dedicated backend method: zaviago_backend.api.item_list.list_items_with_variants
   * Returns items with variants attached to parent rows.
   */
  listWithVariants(params: ItemListWithVariantsParams): Promise<FrappeListResponse<ItemWithVariants>> {
    const { filters, or_filters, pageIndex = 0, pageSize = 20, orderBy, fields } = params;
    
    return docTypeListWithPaginationViaMethod<ItemWithVariants>({
      doctype: "Item",
      filters,
      or_filters,
      pageIndex,
      pageSize,
      orderBy,
      method: "zaviago_backend.api.item_list.list_items_with_variants",
      fields: fields || [...ITEM_LIST_FIELDS],
    });
  },
};

